import axios from 'axios';
import { getProducersQuery, getRouteSessionsQuery, getDriversQuery, getTrailersQuery, getProcessorsQuery } from './queries';
import { getAPIHost, getExpressAPIHost, numberFormat, renameKey, resolveId } from '../../utils';
import { buildExpressQuery, buildLoopbackQuery } from './queries/builders';
import Split from '../split';

// Per-type split selection. Mirrors the splits used by the corresponding
// repository functions (e.g. addPickup/editPickup use use_express_pickup_endpoint)
// so that getSingleInstance fetches from the same backend as the rest of the
// entity's flows within a given region.
const SPLIT_BY_TYPE = {
    pickups: 'use_express_pickup_endpoint',
    dropoffs: 'use_express_dropoff_endpoint',
    'lab-reports': 'use_express_lab_endpoint',
    'route-sessions': 'use_express_route_session_endpoint',
};

// Per-nested-query split. Each nested resource has its own split because
// they are owned by different backends (e.g. /users is gated by the auth
// split, /trailers by the migration split). Reusing the parent type's
// queryBuilder would misroute these lookups in regions where the splits
// differ.
const NESTED_SPLIT = {
    producers: 'use_express_auth_endpoints',
    drivers: 'use_express_auth_endpoints',
    processors: 'use_express_auth_endpoints',
    trailers: 'use_express_migration_endpoints',
    'route-sessions': 'use_express_route_session_endpoint',
};

// Resolve a query builder for the given nested resource. Selects the
// builder independently from the parent type's split so nested lookups
// hit the backend that owns the nested resource.
const builderForNested = (kind) => {
    const split = NESTED_SPLIT[kind] || 'use_express_migration_endpoints';
    return Split.getBooleanTreatment(split) ? buildExpressQuery : buildLoopbackQuery;
};

const getSingleInstance = (type, id) =>
    new Promise((resolve, reject) => {
        // Select split per resource type so getSingleInstance aligns with
        // the backend used by other flows for that type (e.g. addPickup uses
        // use_express_pickup_endpoint, not use_express_migration_endpoints).
        // Falls back to the migration split for types without a dedicated split.
        const splitName = SPLIT_BY_TYPE[type] || 'use_express_migration_endpoints';
        const useExpress = Split.getBooleanTreatment(splitName);
        const host = useExpress ? getExpressAPIHost() : getAPIHost();
        const queryBuilder = useExpress ? buildExpressQuery : buildLoopbackQuery;
        const request = axios.get(`${host}/${type}/${id}`);

        request
            .then((response) => {
                const data = useExpress ? resolveId(response.data) : response.data;
                if (type === 'lab-reports') {
                    const subQuery = getProducersQuery({ ids: [data.producer_id], queryBuilder: builderForNested('producers') });
                    const subRequest = axios.get(subQuery);
                    return subRequest.then((subResponse) => {
                        // Guard against empty array (e.g. soft-deleted producer)
                        const producerName = Array.isArray(subResponse.data) && subResponse.data.length > 0 ? subResponse.data[0].name : null;
                        const merged = { ...data, producer: producerName };
                        resolve({ labReport: [merged] });
                    });
                }
                if (type === 'route-sessions') {
                    resolve({ routeSession: [data] });
                }
                if (type === 'pickups') {
                    const producerSubQuery = getProducersQuery({ ids: [data.producer_id], queryBuilder: builderForNested('producers') });
                    const producerSubRequest = axios.get(producerSubQuery);
                    const driverSubQuery = getDriversQuery({ ids: [data.driver_id], queryBuilder: builderForNested('drivers') });
                    const driverSubRequest = axios.get(driverSubQuery);
                    const sessionQuery = getRouteSessionsQuery({ ids: [data.route_session_id], filterDeleted: true, queryBuilder: builderForNested('route-sessions') });
                    const sessionSubRequest = axios.get(sessionQuery);
                    return sessionSubRequest.then((sessionSubResponse) => {
                        // Guard against empty array (e.g. soft-deleted session)
                        // before reading .trailer_id — otherwise data[0] is undefined
                        // and the next .trailer_id access throws.
                        const session = Array.isArray(sessionSubResponse.data) && sessionSubResponse.data.length > 0 ? sessionSubResponse.data[0] : null;
                        if (!session) {
                            const subRequests = [producerSubRequest, driverSubRequest];
                            return axios.all(subRequests).then(
                                axios.spread((...responses) => {
                                    const [producerResponse, driverResponse] = responses;
                                    const merged = {
                                        ...data,
                                        producer: producerResponse.data,
                                        driver: driverResponse.data,
                                        trailer: null,
                                    };
                                    resolve({ pickup: [merged] });
                                })
                            );
                        }
                        const trailerQuery = getTrailersQuery({ id: session.trailer_id, filterDeleted: true, queryBuilder: builderForNested('trailers') });
                        const trailerSubRequest = axios.get(`${trailerQuery}`);
                        return trailerSubRequest.then((trailerSubResponse) => {
                            // Same guard for trailer — filterDeleted: true can return
                            // an empty array for soft-deleted trailers.
                            const trailer = Array.isArray(trailerSubResponse.data) && trailerSubResponse.data.length > 0 ? numberFormat(parseFloat(trailerSubResponse.data[0].trailer_number), 0) : null;
                            const subRequests = [producerSubRequest, driverSubRequest];
                            return axios.all(subRequests).then(
                                axios.spread((...responses) => {
                                    const [producerResponse, driverResponse] = responses;
                                    const merged = {
                                        ...data,
                                        producer: producerResponse.data,
                                        driver: driverResponse.data,
                                        trailer,
                                    };
                                    resolve({ pickup: [merged] });
                                })
                            );
                        });
                    });
                }
                if (type === 'dropoffs') {
                    const processorSubQuery = getProcessorsQuery({ ids: [data.processor_id], queryBuilder: builderForNested('processors') });
                    const processorSubRequest = axios.get(processorSubQuery);
                    const driverSubQuery = getDriversQuery({ ids: [data.driver_id], queryBuilder: builderForNested('drivers') });
                    const driverSubRequest = axios.get(`${driverSubQuery}`);
                    const sessionRequest = getRouteSessionsQuery({ ids: [data.route_session_id], filterDeleted: true, queryBuilder: builderForNested('route-sessions') });
                    const sessionSubRequest = axios.get(`${sessionRequest}`);
                    return sessionSubRequest.then((sessionSubResponse) => {
                        // Guard against empty array before reading .trailer_id / .BOL.
                        const session = Array.isArray(sessionSubResponse.data) && sessionSubResponse.data.length > 0 ? sessionSubResponse.data[0] : null;
                        if (!session) {
                            const subRequests = [processorSubRequest, driverSubRequest];
                            return axios.all(subRequests).then(
                                axios.spread((...responses) => {
                                    const [processorResponse, driverResponse] = responses;
                                    const merged = {
                                        ...data,
                                        processor: processorResponse.data,
                                        driver: driverResponse.data,
                                        trailer: null,
                                        bol: null,
                                    };
                                    resolve({ dropoff: [merged] });
                                })
                            );
                        }
                        const trailerQuery = getTrailersQuery({ id: session.trailer_id, filterDeleted: true, queryBuilder: builderForNested('trailers') });
                        const trailerSubRequest = axios.get(`${trailerQuery}`);
                        return trailerSubRequest.then((trailerSubResponse) => {
                            const trailer = Array.isArray(trailerSubResponse.data) && trailerSubResponse.data.length > 0 ? numberFormat(parseFloat(trailerSubResponse.data[0].trailer_number), 0) : null;
                            const subRequests = [processorSubRequest, driverSubRequest];
                            return axios.all(subRequests).then(
                                axios.spread((...responses) => {
                                    const [processorResponse, driverResponse] = responses;
                                    const merged = {
                                        ...data,
                                        processor: processorResponse.data,
                                        driver: driverResponse.data,
                                        trailer,
                                        bol: session.BOL,
                                    };
                                    resolve({ dropoff: [merged] });
                                })
                            );
                        });
                    });
                }
            })
            .catch((errors) => {
                // eslint-disable-next-line no-console
                console.warn(errors);
                reject(new Error(errors.toString()));
            });
    });

export default getSingleInstance;
